import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Signal, computed, inject, signal } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdLifeCross } from '../basic-properties/hd-chart-properties.util';
import {
  HD_NODAL_ENVIRONMENT_PAIRS,
  HdNodalEnvironmentPair,
  hdNodalEnvironmentKey,
  hdNodalEnvironmentLabel,
  hdNodalEnvironmentPairForGate,
} from './hd-nodal-environments-metadata';
import { HdNodalEnvironmentsInfoService } from './hd-nodal-environments-info.service';
import { HdNodalLineCardComponent, HdNodalLineSide } from '../nodal-lines/hd-nodal-line-card.component';
import { getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';

/** Paneel-data: het levenskruis van de actieve chart voedt de "jouw omgeving"-markeringen. */
export interface HdNodalEnvironmentsInfoPanelData {
  readonly lifeCross: HdLifeCross | null;
  /**
   * Gezet wanneer het levenskruis van een cycle chart komt (Cycles-sectie in
   * het Geavanceerd-paneel), bv. "Solar return (27 september 2026, 14:03)":
   * het paneel toont dan onder de titel van welk moment de markeringen zijn.
   * Het paneel deelt één key met het natal-levenskruis, dus zonder deze regel
   * is niet te zien welke van de twee er open staat.
   */
  readonly cycleLabel?: string;
}

/** Welke kant(en) van het levenskruis op een omgeving-as landen. */
type OwnSide = 'personality' | 'design' | 'both';

/** Eén eigen levenskruis-kant op een omgeving-as, met de zuid→noord-richting. */
interface OwnEnvironmentRef {
  readonly key: string;
  readonly side: OwnSide;
  readonly southGate: number;
  readonly northGate: number;
}

/** Eén stuk van een richtinglabel; het zuid- en noordgetal dragen hun node-glyph. */
interface DirectionLabelPart {
  readonly text: string;
  /** Astronomicon-teken van de zuid- of noord-node, getoond vóór het poortgetal. */
  readonly glyph?: string;
}

interface DirectionLabelVm {
  /** Volledige richtinglabel als platte tekst, bv. "Persoonlijkheid van 58 naar 52". */
  readonly label: string;
  /** Hetzelfde label in stukken, zodat de template de node-glyphs bij de getallen kan zetten. */
  readonly parts: readonly DirectionLabelPart[];
}

interface OwnEnvironmentVm extends DirectionLabelVm {
  readonly key: string;
}

/** Eén lijnkaart van het eigen levenskruis (zuid- en noord-node delen per kant de lijn). */
interface OwnLineVm {
  readonly side: HdNodalLineSide;
  readonly line: number;
}

interface EnvironmentSectionVm {
  readonly key: string;
  readonly label: string;
  /** Keynotes van beide poorten ("Creativiteit / Ontvankelijkheid"); leeg tot de gates-scope geladen is. */
  readonly subtitle: string;
  readonly url: string;
  /** Richtinglabels van de eigen levenskruis-kanten op deze as (0, 1 of 2 stuks). */
  readonly ownBadges: readonly DirectionLabelVm[];
}

const SOUTH_NODE_GLYPH = getHdPlanetDisplayConfig('southNode').astronomiconChar;
const NORTH_NODE_GLYPH = getHdPlanetDisplayConfig('northNode').astronomiconChar;

/**
 * Gedockt/zwevend info-paneel met de 32 nodale omgevingen: eerst het algemene
 * document, dan de lijnkaarten van het eigen levenskruis ("Jouw
 * omgevingslijnen", zelfde kaarten als de Nodal lines cheat sheet), dan de
 * intro "De 32 omgevingen" en één sectie per omgeving-paar. De markdown-
 * documenten komen per contenttaal uit `assets/docs/human-design/nodal-environments/`
 * (zelfde patroon als {@link HdRelationInfoPanelComponent}); de secties van het
 * eigen levenskruis krijgen een markering plus een snelkoppeling bovenaan.
 *
 * De lijninfo staat bewust vóór de 32 omgevingen: eerst de lijnthematiek lezen,
 * die meespeelt in de specifieke poortthema's van de omgeving. Bij het
 * incarnatiekruis tonen we geen lijninfo; daar is de lijn het profiel, dat al
 * een eigen paneel heeft.
 *
 * Standaard tonen enkel de eigen levenskruis-assen hun sectie; een schakelknop
 * toont alle 32. Zonder levenskruis-data (geen nodes) vallen we terug op alles.
 */
@Component({
  selector: 'hd-nodal-environments-info-panel',
  standalone: true,
  imports: [MarkdownComponent, HdNodalLineCardComponent, NgTemplateOutlet],
  templateUrl: './hd-nodal-environments-info-panel.component.html',
  styleUrl: './hd-nodal-environments-info-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdNodalEnvironmentsInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdNodalEnvironmentsInfoPanelData | null> | null;
  private readonly infoService = inject(HdNodalEnvironmentsInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly title = computed(() => this.text('title'));

  protected readonly generalUrl = computed(() => this.infoService.markdownUrl('general', this.lang()));

  /** Intro "De 32 omgevingen": apart document zodat de lijnkaarten er net boven passen. */
  protected readonly environmentsIntroUrl = computed(() => this.infoService.markdownUrl('environments', this.lang()));

  /** "Nodale omgeving van het cyclusmoment: …" wanneer het paneel vanuit een cycle chart geopend werd. */
  protected readonly contextLabel = computed(() => {
    const cycleLabel = this.panelData?.()?.cycleLabel?.trim();
    return cycleLabel ? this.text('cycleContextLabel').replaceAll('{cycle}', cycleLabel) : '';
  });

  /**
   * De lijnkaarten van het eigen levenskruis: design eerst, dan personaliteit
   * (cheat-sheet-volgorde). Zuid- en noord-node staan 180° uit elkaar en delen
   * dus per kant de lijn; mocht de data toch afwijken, dan krijgt elke lijn
   * een eigen kaart.
   */
  protected readonly ownLines = computed<readonly OwnLineVm[]>(() => {
    const lifeCross = this.panelData?.()?.lifeCross ?? null;
    if (lifeCross == null) {
      return [];
    }
    const linesOf = (side: HdNodalLineSide): OwnLineVm[] => {
      const { southLine, northLine } = lifeCross[side];
      return [...new Set([southLine, northLine])]
        .filter(line => line >= 1 && line <= 6)
        .map(line => ({ side, line }));
    };
    return [...linesOf('design'), ...linesOf('personality')];
  });

  protected readonly ownLinesTitle = computed(() => this.text('ownLinesTitle'));

  protected readonly ownLinesIntro = computed(() => this.text('ownLinesIntro'));

  protected readonly jumpToOwnLinesLabel = computed(() => this.text('jumpToOwnLinesLabel'));

  /**
   * Eigen levenskruis-kanten per omgeving-as, persoonlijkheid eerst. Gelijke as
   * mét gelijke zuid→noord-richting → één samengevoegde 'both'-entry; gelijke as
   * met omgekeerde richting blijft twee aparte entries (elk hun eigen richting).
   */
  private readonly ownEnvironmentRefs = computed<readonly OwnEnvironmentRef[]>(() => {
    const lifeCross = this.panelData?.()?.lifeCross ?? null;
    if (lifeCross == null) {
      return [];
    }
    const refs: OwnEnvironmentRef[] = [];
    const addSide = (side: 'personality' | 'design', gates: { southGate: number; northGate: number }) => {
      const pair = hdNodalEnvironmentPairForGate(gates.southGate);
      if (!pair) {
        return;
      }
      const key = hdNodalEnvironmentKey(pair);
      const sameDirectionIndex = refs.findIndex(ref =>
        ref.key === key && ref.southGate === gates.southGate && ref.northGate === gates.northGate);
      if (sameDirectionIndex >= 0) {
        refs[sameDirectionIndex] = { ...refs[sameDirectionIndex], side: 'both' };
        return;
      }
      refs.push({ key, side, southGate: gates.southGate, northGate: gates.northGate });
    };
    addSide('personality', lifeCross.personality);
    addSide('design', lifeCross.design);
    return refs;
  });

  /** Snelkoppelingen naar de eigen omgeving-secties, met zuid→noord-richting in het label. */
  protected readonly ownEnvironments = computed<readonly OwnEnvironmentVm[]>(() =>
    this.ownEnvironmentRefs().map(ref => ({ key: ref.key, ...this.directionLabel(ref) })),
  );

  protected readonly ownLabel = computed(() => this.text('ownLabel'));

  protected readonly jumpToOwnLabel = computed(() => this.text('jumpToOwnLabel'));

  /** Alle 32 secties tonen (true) of enkel de eigen levenskruis-assen (false, default). */
  protected readonly showAll = signal(false);

  protected readonly toggleLabel = computed(() =>
    this.text(this.showAll() ? 'showOwnLabel' : 'showAllLabel'),
  );

  private readonly sections = computed<readonly EnvironmentSectionVm[]>(() => {
    const lang = this.lang();
    const ownRefs = this.ownEnvironmentRefs();
    return HD_NODAL_ENVIRONMENT_PAIRS.map(pair => {
      const key = hdNodalEnvironmentKey(pair);
      return {
        key,
        label: hdNodalEnvironmentLabel(pair),
        subtitle: this.subtitleFor(pair, lang),
        url: this.infoService.markdownUrl(key, lang),
        ownBadges: ownRefs.filter(ref => ref.key === key).map(ref => this.directionLabel(ref)),
      };
    });
  });

  /** De gerenderde secties: standaard enkel de eigen assen; zonder levenskruis-data alles. */
  protected readonly visibleSections = computed<readonly EnvironmentSectionVm[]>(() => {
    const sections = this.sections();
    if (this.showAll() || this.ownEnvironmentRefs().length === 0) {
      return sections;
    }
    return sections.filter(section => section.ownBadges.length > 0);
  });

  protected toggleShowAll(): void {
    this.showAll.update(showAll => !showAll);
  }

  protected scrollToSection(key: string): void {
    const section = this.host.nativeElement.querySelector(`[data-env-key="${key}"]`);
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Knop naast de titel: spring voorbij de intro naar de "jouw omgevingen"-strook. */
  protected scrollToOwn(): void {
    const own = this.host.nativeElement.querySelector('.hd-nodal-environments-info__own');
    own?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Knop naast de titel: spring naar de lijnkaarten van het eigen levenskruis. */
  protected scrollToOwnLines(): void {
    const lines = this.host.nativeElement.querySelector('.hd-nodal-environments-info__lines');
    lines?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private subtitleFor(pair: HdNodalEnvironmentPair, lang: string): string {
    const keynotes = pair
      .map(gate => this.gateInfo.gateInfoIn(gate, lang).translations.keynote1)
      .filter(keynote => keynote.length > 0);
    return keynotes.length === pair.length ? keynotes.join(' / ') : '';
  }

  private sideLabel(side: OwnSide): string {
    return this.text(`${side}Label`);
  }

  /**
   * "Persoonlijkheid van ☋58 naar ☊52"-label: kant plus de nodes in zuid→noord-volgorde,
   * elk getal met zijn node-glyph. De volgorde van de stukken volgt de vertaalsjabloon.
   */
  private directionLabel(ref: OwnEnvironmentRef): DirectionLabelVm {
    const placeholders: Record<string, DirectionLabelPart> = {
      '{side}': { text: this.sideLabel(ref.side) },
      '{south}': { text: String(ref.southGate), glyph: SOUTH_NODE_GLYPH },
      '{north}': { text: String(ref.northGate), glyph: NORTH_NODE_GLYPH },
    };
    const parts = this.text('directionLabel')
      .split(/(\{side\}|\{south\}|\{north\})/)
      .filter(piece => piece.length > 0)
      .map(piece => placeholders[piece] ?? { text: piece });
    return { label: parts.map(part => part.text).join(''), parts };
  }

  private text(key: string): string {
    return this.gateInfo.nodalEnvironmentsTextIn(key, this.lang());
  }
}
